import type { ConnectorPermissionLevel } from '@research-assistant/shared';
import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiFetch } from '@/lib/api-fetch.js';

/**
 * Client for the workspace-scoped connector routes (`cti-identity-service`, reached via the gateway
 * at `/v1/connectors/workspaces/:workspaceId`). Phase 2 completion: these replace the interim
 * per-user presence view on the workspace Manage page's Connectors tab. The wire is snake_case; this
 * maps it to camelCase for the SPA. Secrets never cross this boundary in either direction on a read —
 * the list reports presence flags only.
 */

const CONNECTORS_BASE = '/v1/connectors/workspaces';

/** Connector taxonomy the list groups by. Any other server value buckets into `other`. */
export type WorkspaceConnectorKind =
  'tms' | 'vcs' | 'pm' | 'device-farm' | 'other';

const KNOWN_KINDS: readonly WorkspaceConnectorKind[] = [
  'tms',
  'vcs',
  'pm',
  'device-farm',
  'other',
];

/** A workspace connector as the SPA consumes it (camelCase). Presence flags only — no secrets. */
export interface WorkspaceConnector {
  provider: string;
  kind: WorkspaceConnectorKind;
  connected: boolean;
  credentialType: string | null;
  permissionLevel: ConnectorPermissionLevel | null;
  instanceUrl: string | null;
  resolvedFrom: string | null;
  connections: WorkspaceConnectorConnection[];
}

export interface WorkspaceConnectorConnection {
  connectionId: string;
  label: string;
  scope: 'workspace' | 'personal' | 'service' | null;
  credentialType: string | null;
  permissionLevel: ConnectorPermissionLevel | null;
  /** The Allow delete switch; absent when identity did not send it. */
  allowDelete?: boolean;
  shareScope: 'all_workspaces' | 'personal_only' | null;
  backgroundUse: boolean;
}

/** The snake_case shape each list entry returns. */
interface WorkspaceConnectorWire {
  provider: string;
  kind: string;
  connected: boolean;
  credential_type: string | null;
  permission_level: ConnectorPermissionLevel | null;
  instance_url: string | null;
  resolved_from: string | null;
  connections?: WorkspaceConnectorConnectionWire[];
}

interface WorkspaceConnectorConnectionWire {
  connection_id?: string;
  label?: string;
  scope?: string | null;
  credential_type?: string | null;
  permission_level?: ConnectorPermissionLevel | null;
  allow_delete?: boolean | null;
  share_scope?: string | null;
  background_use?: boolean;
}

function connectionScope(
  value: string | null | undefined,
): WorkspaceConnectorConnection['scope'] {
  return value === 'workspace' || value === 'personal' || value === 'service'
    ? value
    : null;
}

function shareScope(
  value: string | null | undefined,
): WorkspaceConnectorConnection['shareScope'] {
  return value === 'all_workspaces' || value === 'personal_only' ? value : null;
}

function connectionFromWire(
  wire: WorkspaceConnectorConnectionWire,
): WorkspaceConnectorConnection {
  return {
    connectionId: wire.connection_id ?? '',
    label: wire.label ?? 'Connection',
    scope: connectionScope(wire.scope),
    credentialType: wire.credential_type ?? null,
    permissionLevel:
      wire.permission_level === 'read' || wire.permission_level === 'read_write'
        ? wire.permission_level
        : null,
    ...(typeof wire.allow_delete === 'boolean'
      ? { allowDelete: wire.allow_delete }
      : {}),
    shareScope: shareScope(wire.share_scope),
    backgroundUse: wire.background_use === true,
  };
}

function normalizeKind(kind: string): WorkspaceConnectorKind {
  return (KNOWN_KINDS as readonly string[]).includes(kind)
    ? (kind as WorkspaceConnectorKind)
    : 'other';
}

function connectorFromWire(wire: WorkspaceConnectorWire): WorkspaceConnector {
  return {
    provider: wire.provider,
    kind: normalizeKind(wire.kind),
    connected: wire.connected,
    credentialType: wire.credential_type,
    permissionLevel: wire.permission_level,
    instanceUrl: wire.instance_url,
    resolvedFrom: wire.resolved_from,
    connections: (wire.connections ?? []).map(connectionFromWire),
  };
}

/** Content type only — `apiFetch` attaches the bearer from the renewal core. */
// eslint-disable-next-line @typescript-eslint/require-await
async function headers(): Promise<Record<string, string>> {
  return { 'Content-Type': 'application/json' };
}

async function errorMessage(res: Response, fallback: string): Promise<string> {
  const text = await res.text().catch(() => '');
  if (!text.trim()) return `${fallback} (${res.status})`;
  try {
    const body = JSON.parse(text) as { message?: string | string[] };
    if (Array.isArray(body.message)) return body.message.join('; ');
    if (typeof body.message === 'string' && body.message.trim())
      return body.message;
  } catch {
    /* not JSON */
  }
  return text.trim();
}

/**
 * List a workspace's connectors. The response is wrapped — `{ connectors: [...] }` — so this reads
 * `body.connectors`, never a bare array.
 */
export async function listWorkspaceConnectors(
  workspaceId: string,
): Promise<WorkspaceConnector[]> {
  const res = await apiFetch(
    `${CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}`,
    {
      headers: await headers(),
    },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to load connectors'));
  const body = (await res.json()) as { connectors?: WorkspaceConnectorWire[] };
  return (body.connectors ?? []).map(connectorFromWire);
}

/**
 * Fetch the OAuth authorize URL for a workspace connector. `permission_level` is always sent
 * (`read` or `read_write`), and `allow_delete` whenever the caller knows it, so a reconnect keeps the
 * access the connection has; the caller opens the returned `auth_url` in the popup/redirect flow.
 */
export async function getWorkspaceConnectorAuthUrl(
  workspaceId: string,
  provider: string,
  permissionLevel: ConnectorPermissionLevel,
  connectionId?: string | null,
  allowDelete?: boolean,
): Promise<{ auth_url: string }> {
  const params = new URLSearchParams({ permission_level: permissionLevel });
  if (connectionId) params.set('connection_id', connectionId);
  if (allowDelete !== undefined)
    params.set('allow_delete', String(allowDelete));
  const res = await apiFetch(
    `${CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/${encodeURIComponent(
      provider,
    )}/authorize?${params.toString()}`,
    { headers: await headers() },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to get authorize URL'));
  return (await res.json()) as { auth_url: string };
}

export async function updateWorkspaceConnectorConnection(
  workspaceId: string,
  provider: string,
  connectionId: string,
  patch: { backgroundUse?: boolean },
): Promise<void> {
  const body: Record<string, unknown> = {};
  if (patch.backgroundUse !== undefined) {
    body.background_use = patch.backgroundUse;
  }
  const res = await apiFetch(
    `${CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/${encodeURIComponent(
      provider,
    )}/connections/${encodeURIComponent(connectionId)}`,
    {
      method: 'PATCH',
      headers: await headers(),
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to update connection'));
  }
}

/**
 * Store an api-key credential for a workspace connector. The body shape is provider-specific
 * (snake_case wire fields the identity DTO declares) and is passed through verbatim by the caller.
 */
export async function saveWorkspaceConnectorCredentials(
  workspaceId: string,
  provider: string,
  body: Record<string, string>,
): Promise<void> {
  const res = await apiFetch(
    `${CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/${encodeURIComponent(
      provider,
    )}/credentials`,
    { method: 'POST', headers: await headers(), body: JSON.stringify(body) },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to save credentials'));
}

/**
 * Disconnect ONE named connection of a workspace connector. The service answers `{ ok: true }`.
 *
 * `connectionId` travels as the `connection_id` query param and is required — a provider can hold both
 * the workspace's shared connection and the caller's personal one, so the service answers 400 rather
 * than picking one. The caller takes the id off the presence entry.
 */
export async function disconnectWorkspaceConnector(
  workspaceId: string,
  provider: string,
  connectionId: string,
): Promise<void> {
  const res = await apiFetch(
    `${CONNECTORS_BASE}/${encodeURIComponent(workspaceId)}/${encodeURIComponent(
      provider,
    )}?connection_id=${encodeURIComponent(connectionId)}`,
    { method: 'DELETE', headers: await headers() },
  );
  if (!res.ok && res.status !== 204) {
    throw new Error(await errorMessage(res, 'Failed to disconnect connector'));
  }
}

/** Query-key factory so mutations and the OAuth flow can invalidate the list precisely. */
export const workspaceConnectorKeys = {
  list: (workspaceId: string) => ['workspace-connectors', workspaceId] as const,
};

/** TanStack Query factory for a workspace's connector list. */
export function workspaceConnectorsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: workspaceConnectorKeys.list(workspaceId),
    queryFn: () => listWorkspaceConnectors(workspaceId),
    staleTime: 30_000,
  });
}

export function useSaveWorkspaceConnectorCredentials(
  workspaceId: string,
): UseMutationResult<
  void,
  Error,
  { provider: string; body: Record<string, string> }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { provider: string; body: Record<string, string> }) =>
      saveWorkspaceConnectorCredentials(workspaceId, args.provider, args.body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: workspaceConnectorKeys.list(workspaceId),
      });
    },
  });
}

/**
 * Disconnect mutation. The variable is the pair `{ provider, connectionId }`, not a bare provider: the
 * route names one connection, so a caller that only knows the provider cannot form the request.
 */
export function useDisconnectWorkspaceConnector(
  workspaceId: string,
): UseMutationResult<void, Error, { provider: string; connectionId: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { provider: string; connectionId: string }) =>
      disconnectWorkspaceConnector(
        workspaceId,
        args.provider,
        args.connectionId,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: workspaceConnectorKeys.list(workspaceId),
      });
    },
  });
}
